import { hasCompartmentVariants, hasHotspotCameraAssets, hasInsolationData, hasNamedCamera } from '../../../shared/utils/productUtils.js'
import { FETCH_STATUS } from '../../../shared/constants/fetchStatus.js'

// ── Helpers ────────────────────────────────────
function asArray(value) {
  return Array.isArray(value) ? value : []
}

function getProductTierRefs(product) {
  const refs = Array.isArray(product?.tiers)
    ? product.tiers
        .map(tier => Number(tier?.ref_tier)) //TODO: camelCase after normalization
        .filter(Number.isFinite)
    : []

  if (refs.length === 0) {
    const fallback = Number(product?.ref_tier) //TODO: camelCase after normalization

    if (Number.isFinite(fallback)) {
      refs.push(fallback)
    }
  }

  return [...new Set(refs)].sort((a, b) => a - b)
}

function getProductTierSetKey(product) {
  return getProductTierRefs(product).join('|')
}

function getBuildingPhaseKey(building) {
  const phaseName = String(
    building?.phase_name ?? '', //TODO: camelCase after normalization
  ).trim()

  if (phaseName) {
    return `name:${phaseName}`
  }

  if (building?.currently_on_sale === true) { //TODO: camelCase after normalization
    return 'sale:current'
  }

  if (building?.currently_on_sale === false) { //TODO: camelCase after normalization
    return 'sale:other'
  }

  return null
}

function detectProductsInMultiplePhases(buildings, products) {
  const buildingIdsWithProducts = new Set(
    products
      .map(product => product?.buildingId) 
      .filter(Boolean),
  )

  const phaseKeys = new Set(
    buildings
      .filter(building =>
        buildingIdsWithProducts.has(building?.id_cad), //TODO: camelCase after normalization
      )
      .map(getBuildingPhaseKey)
      .filter(Boolean),
  )

  return phaseKeys.size > 1
}

function normalizeArea(value) {
  const area = Number(value)
  return Number.isFinite(area) ? Math.round(area * 100) / 100 : null
}

function hasMultipleDistinctValues(items, getValue) {
  const values = new Set()

  for (const item of items) {
    const value = getValue(item)
    if (value == null || value === '') continue

    values.add(value)

    if (values.size > 1) {
      return true
    }
  }

  return false
}

// ── Null object — returned before config is available ─────────────────────────
export const NULL_INVENTORY_CAPABILITIES = Object.freeze({
    axonometryAvailable: false,
    axonometryCommissioned: false,
    compartmentTypesAvailable: false,
    compartmentVariantsAvailable: false,
    compartmentVariantsCommissioned: false,
    hasInteriorAssets: false,
    hasMultipleAreas: false,
    hasMultipleBuildings: false,
    hasMultipleGates: false,
    hasMultiLevelProducts: false,
    hasProductsInMultiplePhases: false,
    hasProductsWithAxonometry: false,
    hasProductsWithCompartmentVariants: false,
    hasProductsWithHotspots: false,
    hasProductsWithInsolation: false,
    hotspotsAvailable: false,
    insolationAvailable: false,
    insolationCommissioned: false,
    interiorsCommissioned: false,
})

// ── Pure derivation ────────────────────────────────────
/**
 * Derives Inventory domain capabilities based on project configuration and panel settings.
 * Pure function — used by `InventoryContext` and unit tests.
 *
 * @param {Object}      params
 * @param {object}      params.buildings
 * @param {object}      params.commissioning
 * @param {object}      params.compartmentsStatus
 * @param {object}      params.compartmentTypes
 * @param {object}      params.interiors
 * @param {object}      params.interiorsStatus
 * @param {object|null} params.products             - displayable main products for dataset capability checks
 * @returns {{
 *  axonometryAvailable:                    boolean,
 *  axonometryCommissioned:                 boolean,
 *  compartmentTypesAvailable:              boolean,
 *  compartmentVariantsAvailable:           boolean,
 *  compartmentVariantsCommissioned:        boolean,
 *  hasInteriorAssets:                      boolean,
 *  hasMultiLevelProducts:                  boolean,
 *  hasMultipleAreas:                       boolean,
 *  hasMultipleBuildings:                   boolean,
 *  hasMultipleGates:                       boolean,
 *  hasProductsInMultiplePhases:            boolean,
 *  hasProductsWithAxonometry:              boolean,
 *  hasProductsWithCompartmentVariants:     boolean,
 *  hasProductsWithInsolation:              boolean,
 *  hasProductsWithHotspots:                boolean,
 *  hotspotsAvailable:                      boolean,
 *  insolationAvailable:                    boolean,
 *  insolationCommissioned:                 boolean,
 *  interiorsCommissioned:                  boolean,
 * }} Inventory capabilities object
 */
export function deriveInventoryCapabilities({
    buildings, //enhanced with phasing data
    commissioning,
    compartmentsStatus,
    compartmentTypes,
    interiors,
    interiorsStatus,
    products, //displayableProducts
}) {

    const compartmentTypesAvailable = 
        compartmentsStatus === FETCH_STATUS.READY &&
        Object.keys(compartmentTypes?? {}).length > 0

    
    const hasInteriorAssets = 
        Object.values(interiors ?? {}).some(
            assets => Array.isArray(assets) && assets.length > 0,
        )
    
    const hasDifferentProductTierSets = new Set(
        products
        .map(getProductTierSetKey)
        .filter(Boolean),
    ).size > 1
    const hasMultiLevelProducts = products.some(
        product => getProductTierRefs(product).length > 1,
    )
    const hasMultipleAreas = hasMultipleDistinctValues(
        products,
        product => normalizeArea(product.area_original), //TODO: areaOriginal after normalization
    )
    const hasMultipleBuildings = hasMultipleDistinctValues(
        products,
        product => product.buildingId,
    )
    const hasMultipleGates = hasMultipleDistinctValues(
        products,
        product => product.id_cad?.split('-')[1] //TODO: idDad after normalization
    )
    const hasProductsInMultiplePhases = detectProductsInMultiplePhases(
        buildings,
        products,
    );

    const {
        insolationCommissioned,
        compartmentVariantsCommissioned,
        axonometryCommissioned,
        interiorsCommissioned,
    } = commissioning

    const hasProductsWithInsolation = asArray(products).some(
        (p) => hasInsolationData(p));
    const hasProductsWithAxonometry = asArray(products).some(
        (p) => hasNamedCamera(p?.tiers, (name) => name === 'axonometric')); //TODO REMOVE MAGIC STRING? THIS STRING IS BASED IN DATA NOT OUR JS...
    const hasProductsWithHotspots = asArray(products).some(
        (p) => hasHotspotCameraAssets(p?.tiers, interiors));
    const hasProductsWithCompartmentVariants = asArray(products).some(
        (p) => hasCompartmentVariants(p));






    const insolationAvailable =
        insolationCommissioned &&
        hasProductsWithInsolation

    const compartmentVariantsAvailable =
        compartmentVariantsCommissioned &&
        hasProductsWithCompartmentVariants

    const axonometryAvailable =
        axonometryCommissioned &&
        hasProductsWithAxonometry

    const hotspotsAvailable =
        interiorsCommissioned &&
        interiorsStatus === FETCH_STATUS.READY &&
        hasProductsWithHotspots


    return {
        axonometryAvailable,
        axonometryCommissioned,
        compartmentTypesAvailable,
        compartmentVariantsAvailable,
        compartmentVariantsCommissioned,
        hasInteriorAssets,
        hasDifferentProductTierSets,
        hasMultiLevelProducts,
        hasMultipleAreas,
        hasMultipleBuildings,
        hasMultipleGates,
        hasProductsInMultiplePhases,
        hasProductsWithAxonometry,
        hasProductsWithCompartmentVariants,
        hasProductsWithHotspots,
        hasProductsWithInsolation,
        hotspotsAvailable,
        insolationAvailable,
        insolationCommissioned,
        interiorsCommissioned,
    }
}